"use client";

import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useEffect, useState } from "react";
import { LuEye, LuEyeOff } from "react-icons/lu";
import { cn } from "~/utils/fontsConfig";
import Spinner from "../themed/spinner";
import { If } from "../v3/Conditional";
import { EventTemplate } from "~/app/community-management/[communitySlug]/event-templates/_components/EventTemplatesList";
import { getAllTemplatesForCommunity } from "~/app/community-management/[communitySlug]/event-templates/actions";
import { EventImage } from "../v3/events-components/EventImage";

type Props = {
    communitySlug: string;
    selectedTemplate: EventTemplate | undefined;
    setSelectedTemplate: (template: EventTemplate | undefined) => void;
};
const TemplateModalSelectorList = ({
    communitySlug,
    selectedTemplate,
    setSelectedTemplate,
}: Props) => {
    const [parent] = useAutoAnimate<HTMLDivElement>();
    const [show, setShow] = useState(false);
    const [templates, setTemplates] = useState<EventTemplate[]>([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);

    useEffect(() => {
        const fetchTemplates = async () => {
            setIsLoadingTemplates(true);
            try {
                const data = await getAllTemplatesForCommunity({
                    communitySlug,
                });
                setTemplates(data);
            } catch (error) {
                console.error("Error fetching templates:", error);
            } finally {
                setIsLoadingTemplates(false);
            }
        };
        fetchTemplates();
    }, [communitySlug]);

    useEffect(() => {
        if (templates && templates.length > 0) {
            setShow(true);
        }
    }, [templates]);

    if (!templates || templates.length === 0) {
        return null;
    }

    if (isLoadingTemplates) {
        return (
            <div className="flex w-full flex-col items-center justify-center">
                <p>Loading event templates...</p>
                <Spinner type="medium" className="mt-2 text-v3-N-300" />
            </div>
        );
    }

    return (
        <div className="mt-1">
            <If condition={!show}>
                <div
                    className="flex cursor-pointer flex-row items-center gap-2"
                    onClick={() => {
                        setShow(true);
                    }}
                >
                    <p className="font-bold">Show templates</p>
                    <span className="flex h-7 items-center">
                        <LuEye className="h-5 w-5" />
                    </span>
                </div>
            </If>
            <If condition={show}>
                <div
                    className="flex cursor-pointer flex-row items-center gap-2"
                    onClick={() => {
                        setShow(false);
                    }}
                >
                    <p className="font-bold">Hide event templates</p>
                    <span className="flex h-7 items-center">
                        <LuEyeOff className="h-5 w-5" />
                    </span>
                </div>
            </If>
            <div
                className={cn(
                    "flex gap-4 overflow-x-auto p-2",
                    show && "h-full min-h-[180px]",
                )}
                ref={parent}
            >
                <If condition={!isLoadingTemplates && show}>
                    {templates?.map((template) => (
                        <div
                            key={template.id}
                            className={cn(
                                "flex cursor-pointer flex-col gap-2 p-2",
                                "border border-v3-N-500",
                                "min-h-[100px] w-full min-w-[250px] max-w-[250px]",
                                "ring-offset-1",
                                selectedTemplate &&
                                    selectedTemplate.id === template.id
                                    ? "rounded ring-2 ring-offset-v3-M500 hover:ring-v3-M500"
                                    : "rounded ring-offset-v3-M500 hover:ring-2 hover:ring-v3-mint",
                            )}
                            onClick={() => {
                                if (selectedTemplate?.id === template.id) {
                                    setSelectedTemplate(undefined);
                                } else {
                                    setSelectedTemplate(template);
                                }
                            }}
                        >
                            <div className="relative h-[100px] w-full rounded-full">
                                <EventImage
                                    event={{
                                        ...template,
                                        image: template.image,
                                    }}
                                    options={{
                                        type: "cover",
                                    }}
                                    isReadonly
                                    allowAnimations
                                />
                            </div>
                            <p className="line-clamp-2">{template.name}</p>
                        </div>
                    ))}
                </If>
            </div>
        </div>
    );
};

export default TemplateModalSelectorList;
